"use client";

import { useActionState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import type { AuthState } from "@/app/actions/auth";

type Mode = "signup" | "login";

type AuthFormProps = {
  mode: Mode;
  action: (prev: AuthState, formData: FormData) => Promise<AuthState>;
};

const COPY = {
  signup: {
    title: "Create your account",
    submit: "Sign up",
    footer: "Already have an account?",
    footerHref: "/login",
    footerLink: "Log in",
  },
  login: {
    title: "Welcome back",
    submit: "Log in",
    footer: "Don't have an account?",
    footerHref: "/signup",
    footerLink: "Sign up",
  },
} as const;

const inputClass =
  "h-9 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50";

export function AuthForm({ mode, action }: AuthFormProps) {
  const [state, formAction, pending] = useActionState(action, undefined);
  const copy = COPY[mode];

  return (
    <div className="w-full max-w-sm rounded-3xl border border-border bg-card p-8 shadow-xl shadow-primary/5">
      <h1 className="text-2xl font-bold tracking-tight">{copy.title}</h1>

      <form action={formAction} className="mt-6 flex flex-col gap-4">
        {mode === "signup" && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="name" className="text-sm font-medium">
              Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              required
              className={inputClass}
            />
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-sm font-medium">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className={inputClass}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="password" className="text-sm font-medium">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            required
            minLength={8}
            className={inputClass}
          />
        </div>

        {state?.error && (
          <p
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {state.error}
          </p>
        )}

        <Button type="submit" size="lg" disabled={pending} className="mt-2 w-full">
          {pending ? "Please wait…" : copy.submit}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {copy.footer}{" "}
        <Link href={copy.footerHref} className="font-medium text-primary hover:underline">
          {copy.footerLink}
        </Link>
      </p>
    </div>
  );
}
